import React from 'react';
import * as Sentry from '@sentry/react';

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

/**
 * Error boundary scoped to inspection routes (/inspections/:id, modules, etc).
 *
 * When a render error happens inside the inspection page, it:
 * - Keeps the user on the same route (no navigation)
 * - Shows a non-blocking popup overlay explaining that something went wrong
 * - Offers actions to stay on the page or reload the entire app
 *
 * This reduces the need for a full page refresh initiated by the browser,
 * which helps avoid losing captured data that is already in memory/persisted.
 */
export class InspectionErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    try {
      Sentry.captureException(error, {
        extra: {
          componentStack: errorInfo.componentStack,
          scope: 'inspection-route',
        },
      });
    } catch {
      // Sentry might not be initialized yet; fail silently.
    }
    // eslint-disable-next-line no-console
    console.error('Unhandled error in inspection route:', error, errorInfo);
  }

  private handleStayHere = () => {
    // Clear the error so React tries to re-render children.
    // If the bug is transient (e.g. data race), this can recover without a full reload.
    this.setState({ hasError: false, error: null });
  };

  private handleReloadPage = () => {
    window.location.reload();
  };

  render() {
    const { hasError } = this.state;

    return (
      <>
        {/* When in error, we hide the broken subtree but keep the route and shell intact */}
        {!hasError && this.props.children}

        {hasError && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
            }}
          >
            <div
              role="alertdialog"
              aria-modal="true"
              style={{
                maxWidth: '420px',
                width: '90%',
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow:
                  '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
              }}
            >
              <h2
                style={{
                  margin: 0,
                  marginBottom: '0.75rem',
                  fontSize: '1.2rem',
                  fontWeight: 600,
                }}
              >
                Something went wrong on this inspection
              </h2>
              <p
                style={{
                  margin: 0,
                  marginBottom: '0.75rem',
                  fontSize: '0.95rem',
                  color: '#444',
                }}
              >
                Your captured data is not discarded. You can stay on this page and try
                again, or reload the app if the problem continues.
              </p>
              <p
                style={{
                  margin: 0,
                  marginBottom: '1.25rem',
                  fontSize: '0.85rem',
                  color: '#666',
                }}
              >
                If this keeps happening, please report it so we can investigate.
              </p>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                }}
              >
                <button
                  type="button"
                  onClick={this.handleStayHere}
                  style={{
                    padding: '0.55rem 1.1rem',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    borderRadius: '999px',
                    border: '1px solid #1976d2',
                    backgroundColor: '#fff',
                    color: '#1976d2',
                    cursor: 'pointer',
                  }}
                >
                  Stay on this page
                </button>
                <button
                  type="button"
                  onClick={this.handleReloadPage}
                  style={{
                    padding: '0.55rem 1.1rem',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    borderRadius: '999px',
                    border: 'none',
                    backgroundColor: '#1976d2',
                    color: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  Reload app
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
}

